
import { useEffect, useState } from "react";
import { Select } from "antd";
import Cookies from "js-cookie";
import { Link, useSearchParams } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import { useUserPermissions } from "../../../UserPermissionsContext";

interface ClassItem {
    class_name_id: number;
    name: string;
}

interface OrientationItem {
    orientation_id: number;
    name: string;
}

interface ApiRecord {
    id: number;
    branch: number;
    branch_class: number | null;
    orientation: number | null;
    is_finalized: boolean;
    is_active: boolean;
}

interface ApiResponse {
    total_count: number;
    total_pages: number;
    next_page: string | null;
    previous_page: string | null;
    current_page_number: number;
    next_page_number: number | null;
    previous_page_number: number | null;
    default_page_size: number;
    from: number;
    to: number;
    status: number;
    message: string;
    results: ApiRecord[];
}

interface RowData {
    recordId?: number;
    branch_class: number | null;
    orientation: number | null;
    is_finalized?: boolean;
    isNew?: boolean;
}

const WizklubBranchClassOrientation = () => {
    const [searchParams] = useSearchParams();
    const branchId = searchParams.get("id");

    const { webApi } = useUserPermissions();
    const jwtToken = Cookies.get("authToken");
    const [rows, setRows] = useState<RowData[]>([]);
    const [classList, setClassList] = useState<ClassItem[]>([]);
    const [orientationList, setOrientationList] = useState<
        OrientationItem[]
    >([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const options = {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    };
    /*
     * ---------------------------------------------------------
     * GET CLASS LIST
     * ---------------------------------------------------------
     */
    const getClassList = async () => {
        try {
            const token = Cookies.get("authToken");

            const response = await fetch(
                `${webApi}/wizklub/classes_dropdown_for_branch_selection/`,
                options
            );

            const data = await response.json();

            if (response.ok) {
                setClassList(data || data || []);
            } else {
                console.error("Class API error:", data);
            }
        } catch (error) {
            console.error("Error fetching classes:", error);
        }
    };

    /*
     * ---------------------------------------------------------
     * GET ORIENTATION LIST
     * ---------------------------------------------------------
     */
    const getOrientationList = async () => {
        try {
            const token = Cookies.get("token");

            const response = await fetch(
                `${webApi}/wizklub/orientations_dropdown_for_branch_selection/?branch_id=${branchId}`,
                options
            );

            const data = await response.json();

            if (response.ok) {
                setOrientationList(data?.results || data || []);
            } else {
                console.error("Orientation API error:", data);
            }
        } catch (error) {
            console.error("Error fetching orientations:", error);
        }
    };

    /*
     * ---------------------------------------------------------
     * GET EXISTING CLASS + ORIENTATION RECORDS
     * ---------------------------------------------------------
     */
    const getBranchClassOrientation = async () => {
        if (!branchId) return;

        setLoading(true);

        try {

            const response = await fetch(
                `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/?branch_id=${branchId}`,
                options
            );

            const data: ApiResponse = await response.json();

            if (response.ok && data?.results) {
                /*
                 * IMPORTANT:
                 * One API record = One UI row
                 */
                const formattedRows: RowData[] = data.results
                    .filter((item) => item.is_active !== false)
                    .map((item) => ({
                        recordId: item.id,
                        branch_class: item.branch_class,
                        orientation: item.orientation,
                        is_finalized: item.is_finalized,
                        isNew: false,
                    }));

                setRows(formattedRows);
            } else {
                setRows([]);
            }
        } catch (error) {
            console.error(
                "Error fetching branch class orientation:",
                error
            );
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * INITIAL LOAD
     * ---------------------------------------------------------
     */
    useEffect(() => {
        getClassList();
        getOrientationList();
    }, []);

    useEffect(() => {
        if (branchId) {
            getBranchClassOrientation();
        }
    }, [branchId]);

    /*
     * ---------------------------------------------------------
     * ADD EMPTY ROW
     * ---------------------------------------------------------
     */
    const handleAddRow = () => {
        setRows((prevRows) => [
            ...prevRows,
            {
                branch_class: null,
                orientation: null,
                isNew: true,
            },
        ]);
    };

    /*
     * ---------------------------------------------------------
     * CLASS CHANGE
     * ---------------------------------------------------------
     */
    const handleClassChange = (
        index: number,
        value: number | null
    ) => {
        setRows((prevRows) =>
            prevRows.map((row, rowIndex) =>
                rowIndex === index
                    ? {
                        ...row,
                        branch_class: value,
                    }
                    : row
            )
        );
    };

    /*
     * ---------------------------------------------------------
     * ORIENTATION CHANGE
     * ---------------------------------------------------------
     */
    const handleOrientationChange = (
        index: number,
        value: number | null
    ) => {
        setRows((prevRows) =>
            prevRows.map((row, rowIndex) =>
                rowIndex === index
                    ? {
                        ...row,
                        orientation: value,
                    }
                    : row
            )
        );
    };

    /*
     * ---------------------------------------------------------
     * SAVE ONE ROW
     *
     * Existing row -> PUT
     * New row -> POST
     * ---------------------------------------------------------
     */
    const handleSaveRow = async (row: RowData, index: number) => {
        if (!branchId) {
            alert("Branch ID is missing");
            return;
        }

        /*
         * Class and orientation are mandatory.
         */
        if (!row.branch_class || !row.orientation) {
            alert("Please select Class and Orientation");
            return;
        }

        setSaving(true);

        try {
            const token = Cookies.get("token");

            let url = "";

            let method = "";

            /*
             * -------------------------------------------------
             * NEW ROW
             * -------------------------------------------------
             */
            if (!row.recordId) {
                url = `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/?branch_id=${branchId}`;

                method = "POST";
            }

            /*
             * -------------------------------------------------
             * EXISTING ROW
             * -------------------------------------------------
             */
            else {
                url = `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/${row.recordId}/?branch_id=${branchId}`;

                method = "PUT";
            }

            const payload = {
                branch: Number(branchId),
                branch_class: row.branch_class,
                orientation: row.orientation,
                is_active: true,
            };

            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
              

                /*
                 * Reload the GET data.
                 *
                 * This is important because after POST the backend
                 * generates the actual record ID.
                 */
                await getBranchClassOrientation();
            } else {
                console.error("Save error:", data);

                alert(
                    data?.message ||
                    data?.detail ||
                    "Unable to save record"
                );
            }
        } catch (error) {
            console.error("Error saving row:", error);
            alert("Something went wrong while saving");
        } finally {
            setSaving(false);
        }
    };

    /*
     * ---------------------------------------------------------
     * DELETE / DEACTIVATE ROW
     *
     * API expects:
     *
     * {
     *   "branch": null,
     *   "branch_class": null,
     *   "orientation": null,
     *   "is_active": false
     * }
     *
     * ---------------------------------------------------------
     */
    const handleDeleteRow = async (
        row: RowData,
        index: number
    ) => {
        /*
         * If it is a newly-added unsaved row,
         * there is no API record yet.
         *
         * So simply remove it from UI.
         */
        if (!row.recordId) {
            setRows((prevRows) =>
                prevRows.filter(
                    (_, rowIndex) => rowIndex !== index
                )
            );

            return;
        }

        if (!branchId) return;

        const confirmDelete = window.confirm(
            "Are you sure you want to deactivate this record?"
        );

        if (!confirmDelete) return;

        try {
            const token = Cookies.get("token");

            const url = `${webApi}/wizklub/create_update_wizklub_branch_class_orientation_selection/${row.recordId}/?branch_id=${branchId}`;

            const payload = {
                branch: null,
                branch_class: null,
                orientation: null,
                is_active: false,
            };

            const response = await fetch(url, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                

                /*
                 * Refresh rows after delete.
                 * Since API returns only active records,
                 * deleted row will disappear.
                 */
                await getBranchClassOrientation();
            } else {
                console.error("Delete error:", data);

                alert(
                    data?.message ||
                    data?.detail ||
                    "Unable to deactivate record"
                );
            }
        } catch (error) {
            console.error("Error deleting row:", error);
            alert("Something went wrong while deleting");
        }
    };

    /*
     * ---------------------------------------------------------
     * CLASS SEARCH
     * ---------------------------------------------------------
     */
    const filterClassOption = (
        input: string,
        option?: {
            label?: string;
            value?: number;
        }
    ) => {
        return (
            option?.label
                ?.toLowerCase()
                .includes(input.toLowerCase()) ?? false
        );
    };

    /*
     * ---------------------------------------------------------
     * ORIENTATION SEARCH
     * ---------------------------------------------------------
     */
    const filterOrientationOption = (
        input: string,
        option?: {
            label?: string;
            value?: number;
        }
    ) => {
        return (
            option?.label
                ?.toLowerCase()
                .includes(input.toLowerCase()) ?? false
        );
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* ------------------------------------------------
                    PAGE HEADER
                ------------------------------------------------ */}
                <div className="page-header">
                    <div className="row align-items-center">
                        <div className="col">
                            <h3 className="page-title">
                                Branch Class & Orientation
                            </h3>
                        </div>

                        <div className="col-auto">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddRow}
                            >
                                <i className="ti ti-plus me-1" />
                                Add
                            </button>
                        </div>
                    </div>
                </div>

                {/* ------------------------------------------------
                    TABLE
                ------------------------------------------------ */}
                <div className="card">
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table table-bordered table-hover">
                                <thead>
                                    <tr>
                                        <th style={{ width: "80px" }}>
                                            S.No
                                        </th>

                                        <th>
                                            Class
                                        </th>

                                        <th>
                                            Orientation
                                        </th>

                                        <th
                                            style={{
                                                width: "180px",
                                            }}
                                        >
                                            Action
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="text-center"
                                            >
                                                Loading...
                                            </td>
                                        </tr>
                                    ) : rows.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={4}
                                                className="text-center"
                                            >
                                                No records found
                                            </td>
                                        </tr>
                                    ) : (
                                        rows.map(
                                            (row, index) => (
                                                <tr
                                                    key={
                                                        row.recordId ??
                                                        `new-${index}`
                                                    }
                                                >
                                                    {/* S.NO */}
                                                    <td>
                                                        {index + 1}
                                                    </td>

                                                    {/* CLASS */}
                                                    <td>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            placeholder="Select Class"
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                            value={
                                                                row.branch_class ??
                                                                undefined
                                                            }
                                                            onChange={(
                                                                value
                                                            ) =>
                                                                handleClassChange(
                                                                    index,
                                                                    value ??
                                                                    null
                                                                )
                                                            }
                                                            filterOption={
                                                                filterClassOption
                                                            }
                                                            optionFilterProp="label"
                                                            options={classList.map(
                                                                (
                                                                    item
                                                                ) => ({
                                                                    value: item.class_name_id,
                                                                    label: item.name,
                                                                })
                                                            )}
                                                        />
                                                    </td>

                                                    {/* ORIENTATION */}
                                                    <td>
                                                        <Select
                                                            showSearch
                                                            allowClear
                                                            placeholder="Select Orientation"
                                                            style={{
                                                                width: "100%",
                                                            }}
                                                            value={
                                                                row.orientation ??
                                                                undefined
                                                            }
                                                            onChange={(
                                                                value
                                                            ) =>
                                                                handleOrientationChange(
                                                                    index,
                                                                    value ??
                                                                    null
                                                                )
                                                            }
                                                            filterOption={
                                                                filterOrientationOption
                                                            }
                                                            optionFilterProp="label"
                                                            options={orientationList.map(
                                                                (
                                                                    item
                                                                ) => ({
                                                                    value: item.orientation_id,
                                                                    label: item.name,
                                                                })
                                                            )}
                                                        />
                                                    </td>

                                                    {/* ACTION */}
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-success btn-sm"
                                                                disabled={
                                                                    saving
                                                                }
                                                                onClick={() =>
                                                                    handleSaveRow(
                                                                        row,
                                                                        index
                                                                    )
                                                                }
                                                            >
                                                                Save
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() =>
                                                                    handleDeleteRow(
                                                                        row,
                                                                        index
                                                                    )
                                                                }
                                                            >
                                                                Delete
                                                            </button>

                                                            {index ===
                                                                rows.length -
                                                                1 && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary btn-sm"
                                                                        onClick={
                                                                            handleAddRow
                                                                        }
                                                                    >
                                                                        <i className="ti ti-plus" />
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WizklubBranchClassOrientation;

